/**
 * ConsensusSummary — Phase 07.3 polish.
 *
 * Surfaces War Room (Phase 03) consensus entries on the review panel.
 * Collapsible — header shows entry count + dissent indicator; expanding
 * reveals per-field resolved value + resolution method + per-agent dissent.
 *
 * Lifted out of CalculationReviewPanel.tsx so the component is unit-testable
 * in isolation and reusable by Phase 09 attorney overlay. Behavior unchanged
 * from the inline version — pure refactor.
 */

import React, { useId, useState } from 'react';

import type { ConsensusEntry } from '../../../types/engineReview';

export interface ConsensusSummaryProps {
  entries?: ConsensusEntry[];
}

export default function ConsensusSummary({ entries }: ConsensusSummaryProps) {
  const [expanded, setExpanded] = useState(false);
  // Unique per-instance id for aria-controls (Phase 07.8).
  const detailId = useId();

  if (!entries || entries.length === 0) {
    return null;
  }

  const hasDissent = entries.some((e) => e.dissent && e.dissent.length > 0);

  return (
    <div
      className="rounded-lg border p-3"
      style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)' }}
      data-testid="consensus-summary"
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={expanded}
        aria-controls={detailId}
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <span className="text-sm font-medium">
            Consensus: {entries.length} input{entries.length > 1 ? 's' : ''} resolved
          </span>
          {hasDissent && (
            <span className="text-xs font-medium" style={{ color: 'var(--color-brand)' }}>
              (with dissent)
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          style={{ color: 'var(--color-text-muted)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div id={detailId} className="mt-3 space-y-3">
          {entries.map((entry) => (
            <div
              key={entry.field_id}
              className="rounded p-2 border"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
              data-testid={`consensus-entry-${entry.field_id}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {entry.field_name}
                </span>
                <span className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                  {String(entry.resolved_value)}
                </span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                Resolved via: {entry.resolution_method.replace(/_/g, ' ')}
              </p>
              {entry.dissent && entry.dissent.length > 0 && (
                <div
                  className="mt-2 pt-2 border-t -mx-2 -mb-2 px-2 pb-2 rounded-b"
                  style={{ borderTopColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}
                >
                  <p className="text-xs font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Dissent:</p>
                  {entry.dissent.map((d, i) => (
                    <p key={i} className="text-xs mt-1" style={{ color: 'var(--color-needs-human-text)' }}>
                      {d.agent_name}: preferred {String(d.preferred_value)} - {d.reasoning}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
